export default function Hero() {
  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = [
    { label: '每日更新', value: '24小时不间断', icon: 'ri-timer-flash-line' },
    { label: '综艺节目', value: '覆盖热门IP', icon: 'ri-live-line' },
    { label: '高清画质', value: '流畅不缓冲', icon: 'ri-4k-line' },
  ];

  return (
    <section
      id="hero"
      className="relative min-h-[600px] md:min-h-[680px] flex items-center pt-[72px] overflow-hidden bg-gradient-to-br from-primary-50/60 via-background-50 to-accent-50/40"
    >
      {/* Decorative blobs */}
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-primary-100/30 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-accent-100/30 blur-3xl pointer-events-none"></div>

      <div className="w-full px-4 md:px-6 lg:px-10 max-w-[1400px] mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          {/* Left Content */}
          <div className="flex-1 w-full text-center lg:text-left">
            {/* Tag */}
            <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-1.5 rounded-full text-sm font-medium mb-5">
              <i className="ri-fire-line"></i>
              <span className="whitespace-nowrap">热门综艺 · 天天更新</span>
            </div>

            {/* H1 */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-tight mb-4">
              天天影视
              <span className="block text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-700 mt-1">
                高清综艺节目在线观看平台
              </span>
            </h1>

            {/* Slogan */}
            <p className="text-lg md:text-xl text-foreground-600 font-medium mb-6">
              真人秀 · 脱口秀 · 搞笑综艺，精彩不断
            </p>

            {/* Intro paragraphs */}
            <div className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8 space-y-3 max-w-[600px] mx-auto lg:mx-0">
              <p>
                天天影视专注综艺节目在线观看，汇集当下最热门的真人秀、搞笑脱口秀和娱乐视频内容。从《欢乐喜剧人》到《奔跑吧》，从经典老牌综艺到最新上线的热门新作——我们每日同步更新，让你第一时间看到想看的节目。
              </p>
              <p>
                所有综艺节目高清免费，智能加速技术确保播放流畅不缓冲。无论你是喜欢真人秀的紧张刺激，还是脱口秀的轻松解压，这里都有属于你的那一档综艺。
              </p>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 md:gap-8 mb-8">
              {stats.map((stat) => (
                <div key={stat.label} className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className={`${stat.icon} text-primary-600 text-sm`}></i>
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                    <div className="text-sm font-semibold text-foreground-800 whitespace-nowrap">{stat.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <button
                onClick={() => scrollToSection('cards')}
                className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                开始探索综艺
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <button
                onClick={() => scrollToSection('ranking')}
                className="w-full sm:w-auto px-8 py-3.5 rounded-full border-2 border-foreground-200 text-foreground-700 text-base font-semibold hover:border-primary-300 hover:text-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                查看热门榜单
                <i className="ri-bar-chart-line ml-2"></i>
              </button>
            </div>
          </div>

          {/* Right Visual */}
          <div className="flex-1 w-full flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[500px] aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20entertainment%20studio%20with%20vibrant%20warm%20lighting%2C%20TV%20production%20setup%20with%20colorful%20stage%20lights%20in%20coral%20and%20golden%20tones%2C%20professional%20camera%20equipment%2C%20dynamic%20composition%20with%20abstract%20geometric%20shapes%2C%20clean%20minimalist%20aesthetic%2C%20creative%20and%20energetic%20atmosphere&width=1000&height=750&seq=hero-tv-2026&orientation=landscape"
                alt="天天影视综艺节目在线观看平台"
                className="w-full h-full object-cover rounded-2xl"
              />
              {/* Floating badges */}
              <div className="absolute -top-3 -left-3 bg-background-50 rounded-xl px-4 py-2 text-sm font-bold text-primary-600 shadow-sm border border-background-200/70">
                <i className="ri-star-fill text-accent-500 mr-1"></i>
                热门推荐
              </div>
              <div className="absolute -bottom-3 -right-3 bg-background-50 rounded-xl px-4 py-2 text-sm font-bold text-accent-700 shadow-sm border border-background-200/70">
                <i className="ri-play-circle-fill text-primary-500 mr-1"></i>
                高清免费
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}